import { setCarouselEntitiesOutsideInitialView } from "@shared/services/event_tracking/carousel_tracking";
import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import {
  PageEntity,
  RetailerEntity,
  ScreenPositionEntity,
  TargetObjectEntity,
  UserEntity,
} from "@shared/services/event_tracking/entities";
import { TrackingEventStore, Events } from "@shared/svelte/Stores/TrackingEventStore";
import { shouldNotTrack } from "@shared/services/internal_user_check_helpers";
import { v4 as uuidv4 } from "uuid";
import { NewBrowserTabEntity } from "@shared/services/event_tracking/entities";
import { OnboardingSessionStore } from "@onboarding2/svelte/Stores/OnboardingSessionStore";

import { OnboardingSessionEntity } from "./entities/onboarding_session_entity";
import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
import WindowLocationStore from "@shared/svelte/Stores/WindowLocationStore";
import { get as getStoreValue } from "svelte/store";
import { CarrotEvents, trackCarrotEvent } from "@shared/services/event_tracking/carrot_tracking";
import * as Sentry from "@sentry/browser";
import { compact, cloneDeep, chunk, get, debounce } from "lodash";
import { KevelEvents, trackKevelEvent } from "@shared/services/event_tracking/kevel_tracking";
import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";

declare const window: any;

const VALID_EVENT_FORMAT = /^[A-Za-z0-9]+([ _-][A-Za-z0-9]+)*$/;

const trackEvent = (
  event: string,
  entities?: BaseEntityClass | BaseEntityClass[],
  targetObjectType?: string,
) => {
  _validateEventName(event);

  const defaultEntities = _buildDefaultEntities();
  const trackedEntities = _injectTargetObjectEntity(
    ensureEntitiesIsArray(entities),
    targetObjectType,
  );
  const baseEntities: BaseEntityClass[] = ensureEntitiesIsArray(trackedEntities);
  const allEntities: BaseEntityClass[] = defaultEntities.concat(compact(baseEntities));

  _track(event, allEntities, true);
};

const trackClick = (
  targetObjectType: string,
  entities: BaseEntityClass | BaseEntityClass[] = [],
  reportToCarrot: boolean = true,
) => {
  const defaultEntities = _buildDefaultEntities();
  const trackedEntities: BaseEntityClass[] = _injectTargetObjectEntity(
    ensureEntitiesIsArray(entities),
    targetObjectType,
  );

  const baseEntities: BaseEntityClass[] = ensureEntitiesIsArray(trackedEntities);
  const allEntities: BaseEntityClass[] = defaultEntities.concat(compact(baseEntities));

  const isAd = hasAds(baseEntities);
  const event = isAd ? "ad_click" : "click";

  if (reportToCarrot) {
    trackCarrotEvent(CarrotEvents.Click, allEntities);
  }

  trackKevelEvent(KevelEvents.Click, allEntities);

  _track(event, allEntities, true);
};

// This is used with the svelte "use" directive, which must take the node as the first argument.
const trackBeginToRender = (_node: any, trackingArguments: any) => {
  const defaultEntities = _buildDefaultEntities();
  const [entities, extraOptions] = trackingArguments;
  const trackedEntities = _injectTargetObjectEntity(
    ensureEntitiesIsArray(entities),
    extraOptions?.targetObjectType,
  );

  const isAd = hasAds(trackedEntities);
  const event = isAd ? "ad_begin_to_render" : "begin_to_render";

  const baseEntities: BaseEntityClass[] = ensureEntitiesIsArray(trackedEntities);
  const allEntities: BaseEntityClass[] = defaultEntities.concat(compact(baseEntities));

  trackCarrotEvent(CarrotEvents.BeginToRender, allEntities);

  _track(event, allEntities);
};

const createObserver = (
  element: any,
  defaultEntities: any,
  clonedEntities: any,
  event: string,
  carrotEvent: any,
  viewedTime: number,
  viewedThreshold: number,
  extraOptions: any,
) => {
  const viewportTimeouts = {};

  const intersectionObserverOptions: any = {
    rootMargin: "0px",
  };
  if (viewedThreshold) {
    intersectionObserverOptions.threshold = viewedThreshold;
  }

  const checkIfHidden = () => {
    if (extraOptions && extraOptions.checkIfHidden) {
      return extraOptions.checkIfHidden(element);
    } else {
      return false;
    }
  };

  let outsideInitialView = false;
  let belowFold = false;

  return new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) {
        if (checkIfHidden()) {
          outsideInitialView = true;
        }
        if (isBelowFold(element)) {
          belowFold = true;
        }

        // The element has moved out of the viewport and we haven't completed the timeout callback yet
        // Clear the existing timeout until the next time the element is visible
        clearTimeout(viewportTimeouts[entry.target.id]);
        return;
      }
      viewportTimeouts[entry.target.id] = setTimeout(() => {
        observer.unobserve(entry.target);

        clonedEntities = setCarouselEntitiesOutsideInitialView(clonedEntities, outsideInitialView);

        const rect = element.getBoundingClientRect();
        const screenPositionEntity = new ScreenPositionEntity({
          scrollX: window.scrollX,
          scrollY: window.scrollY,
          top: rect.top,
          left: rect.left,
          belowFold: belowFold,
          outsideInitialView: outsideInitialView,
        });

        const allEntities: BaseEntityClass[] = defaultEntities
          .concat(compact(clonedEntities))
          .concat(screenPositionEntity);
        _track(event, allEntities);

        trackCarrotEvent(carrotEvent, allEntities);
        if (carrotEvent === CarrotEvents.ViewableInViewport) {
          trackKevelEvent(carrotEvent, allEntities);
        }
      }, viewedTime);
    });
  }, intersectionObserverOptions);
};

// For function using this to work, each element must have a unique id attribute
const viewedMrcEventMethod = (
  eventBase: string,
  carrotEvent: any,
  viewedTime: number = 0, // How much time the element must be in the viewport to trigger the event (if any)
  viewedThreshold: number = null, // How much of the element must be in the viewport to trigger the event (if any)
) => {
  return (element: any, trackingArguments: any) => {
    const defaultEntities = _buildDefaultEntities();
    const [entities, extraOptions] = trackingArguments;
    const trackedEntities = _injectTargetObjectEntity(
      ensureEntitiesIsArray(entities),
      extraOptions?.targetObjectType,
    );

    const isAd = hasAds(trackedEntities);
    const event = isAd ? `ad_${eventBase}` : eventBase;

    const baseEntities: BaseEntityClass[] = ensureEntitiesIsArray(trackedEntities);
    const clonedEntities: BaseEntityClass[] = cloneDeep(baseEntities);

    const observer = createObserver(
      element,
      defaultEntities,
      clonedEntities,
      event,
      carrotEvent,
      viewedTime,
      viewedThreshold,
      extraOptions,
    );

    observer.observe(element);
  };
};

// Must be 50% visible for 1 second per MRC guidelines
const trackViewableInViewport = viewedMrcEventMethod(
  "viewable_in_viewport",
  CarrotEvents.ViewableInViewport,
  1000,
  0.5,
);
const track1pxInViewport = viewedMrcEventMethod("1px_in_viewport", CarrotEvents.OnePxInViewport);

const trackPageView = (trackNewTab: boolean = false) => {
  let entities = [];
  if (trackNewTab && !sessionStorage.getItem("new_tab_tracked")) {
    entities = [new NewBrowserTabEntity()];
    sessionStorage.setItem("new_tab_tracked", "true");
  }

  const defaultEntities: BaseEntityClass[] = _buildDefaultEntities();
  const allEntities: BaseEntityClass[] = defaultEntities.concat(compact(entities));

  _track("page_view", allEntities, true);
};

export const trackMrcEvents = (element: any, trackingArguments: any) => {
  trackBeginToRender(element, trackingArguments);
  trackViewableInViewport(element, trackingArguments);
  track1pxInViewport(element, trackingArguments);
};

// Useful if the 500ms delay on sending events is too long, IE an event right before window.location change.
const immediatelyProcessEventQueue = (): Promise<any> => _processQueue();

const trackExternalCarrotAddToCart = trackedEntities => {
  trackCarrotEvent(CarrotEvents.AddedToCart, trackedEntities);
};

export const EventTracking = {
  trackMrcEvents,
  trackEvent,
  trackClick,
  trackExternalCarrotAddToCart,
  trackPageView,
  immediatelyProcessEventQueue,
};

// Ads and carrot calls
const hasAds = (entities: BaseEntityClass[]) => {
  return entities.some(e => e.type.split("_").pop() === "ad");
};

const isBelowFold = (elem: any) => {
  try {
    const rect = elem.getBoundingClientRect();
    const yMax = window.innerHeight || document.documentElement.clientHeight;
    return rect.top >= 0 && rect.top > yMax;
  } catch (error) {
    console.error(error);
    return false;
  }
};

// Everything below is for inner workings of .track behavior
let messageQueue = [];

// This method needs to stay in this file so that it is private, only allowing
// users to call the .track<thing> methods directly (e.g. trackClick)
// targetEntity can be a single entity or array of entities
const _track = (
  eventName: string,
  trackingEntities: BaseEntityClass[],
  alwaysSend: boolean = false,
) => {
  const message = _buildMessage(eventName, trackingEntities);

  // views and impressions should not send while state is changing, as the entities for them could get built in half-ready states
  // clicks and events should always send, as they are moments in time triggered by user behavior
  if (!window.isChangingState || alwaysSend) {
    TrackingEventStore.update((prevState: Events) => {
      const events = [message, ...prevState];
      return events;
    });
    // Batch every 500msec and send.
    _sendMessage(message);
  }
};

const _sendMessage = message => {
  if (shouldNotTrack()) {
    return;
  }

  messageQueue.push(message);
  _processQueueDebounced();
};

const _processQueue = (): Promise<any> => {
  const promises = chunk(messageQueue, 10).map(chunk => _sendMessages(chunk));
  messageQueue = [];
  return Promise.all(promises);
};

const _sendMessages = (messages): Promise<any> => {
  if (
    window.RAILS_ENV !== "test" ||
    get(window, "$rootScope.user.email") == "event-tracking-test-user@provi.com"
  ) {
    try {
      messages = JSON.stringify(messages);
      return HttpPromise({
        method: "POST",
        url: "/api/event_stream/send_events",
        data: { messages },
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }
};
const _processQueueDebounced = debounce(_processQueue, 500);

const _buildMessage = (eventName: string, entities: BaseEntityClass[]) => {
  const event = {
    id: uuidv4(),
    name: eventName,
    timestamp: new Date().toISOString(),
  };

  entities.forEach(entity => _validateEntity(eventName, entity));

  const context = {
    sessionId: window.SESSION_ID,
    userAgent: window.navigator.userAgent,
  };

  return {
    event: event,
    entities: entities.filter(e => e !== null && e !== undefined),
    context: context,
  };
};

const _validateEntity = (eventName: string, entity: BaseEntityClass) => {
  try {
    entity.isValid();
  } catch {
    reportInvalidEntityToSentry({
      eventName,
      entity,
      consoleError: "Undeclared EventTracking entity",
      sentryMessage:
        "Entity must be defined in event_tracking/entities, and must extend BaseEntityClass",
    });
  }
  if (!entity.entity_version) {
    reportInvalidEntityToSentry({
      eventName,
      entity,
      consoleError: "Unversioned entity",
      sentryMessage: "Entity must have an entity_version as a defined parameter",
    });
  }
};

// Note: This is called individually in the .trackSomething methods while we are still routing through the old .track method
// Once we delete the old code, this can be done in _buildDefaultEntities instead.
const _injectTargetObjectEntity = (
  entities: BaseEntityClass[],
  targetObjectType: string | null,
): BaseEntityClass[] => {
  if (targetObjectType) {
    return entities.concat(TargetObjectEntity.fromObjectType(targetObjectType));
  } else {
    return entities;
  }
};

const _buildDefaultEntities = (): BaseEntityClass[] => {
  // Non-vite pages would be storing this in window via angular
  let location = window.location;
  let stateParams = window.stateParams;

  // Vite pages will update this store after the page has loaded.
  if (window.modern_mode) {
    const storeValue = getStoreValue(WindowLocationStore);
    location = storeValue.location;
    stateParams = storeValue.stateParams;
  }
  const pageEntity: PageEntity = PageEntity.fromWindowLocation(location, stateParams);

  let retailerEntity: RetailerEntity | null;
  if (window["$rootScope"].currentRetailerId && window["$rootScope"].getCurrentRetailer) {
    retailerEntity = RetailerEntity.fromRetailer(window["$rootScope"].getCurrentRetailer());
  }

  let userEntity: UserEntity | null;
  let onboardingEntity: OnboardingSessionEntity | null;

  let onboardingSession = getStoreValue(OnboardingSessionStore);
  if (onboardingSession?.session_id) {
    onboardingEntity = new OnboardingSessionEntity(onboardingSession);
  }

  let user = getStoreValue(CurrentUserStore);
  if (user) {
    userEntity = UserEntity.fromUser(user);
  }

  return compact([onboardingEntity, pageEntity, retailerEntity, userEntity]);
};

const _validateEventName = (eventName: string) => {
  if (typeof eventName !== "string" || !eventName.match(VALID_EVENT_FORMAT)) {
    throw new Error(`Invalid format for event name: ${eventName}`);
  }
};

const ensureEntitiesIsArray = (
  entities: BaseEntityClass | BaseEntityClass[],
): BaseEntityClass[] => {
  if (Array.isArray(entities)) {
    return entities;
  }
  return [entities];
};

const reportInvalidEntityToSentry = ({ eventName, entity, consoleError, sentryMessage }) => {
  // We fail specs on console errors, so this should never make it to sentry if there are appropriate tests,
  // but if not, we can catch it in prod usage via the sentry messages
  console.error(consoleError);
  const errorDetails = {
    message: sentryMessage,
    eventName,
    entity,
  };

  Sentry.withScope(scope => {
    scope.setExtras(errorDetails);
    Sentry.captureMessage(consoleError);
  });
};
